import { Component, type SyntheticEvent } from "react";
import { pick } from "ramda";
import { observer } from "mobx-react";
import { Stack, TextField, DefaultButton } from "@fluentui/react";
import { action, computed, makeObservable } from "mobx";
import { type SiteUpdate, type Site } from "../../api";
import { doubleBindString } from "../../utils/double-bind";
import { createUuid } from "../../utils/uuid";
import { type FormUpdateProps } from "../../utils/form-update-props";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";

export interface FormUpdateSiteProps extends FormUpdateProps<Site> {}

import { store } from "../../store";

@observer
export class FormUpdateSite extends Component<FormUpdateSiteProps> {
    private validationId = createUuid();

    constructor(props: FormUpdateSiteProps) {
        super(props);
        makeObservable(this);
    }

    private triggerValidation(): void {
        store.repositorySites.validation.update.updateModel(this.validationId, this.siteUpdate);
    }

    componentDidMount(): void {
        store.repositorySites.validation.update.initializeModel(
            this.validationId,
            this.siteUpdate,
            this.props.id,
        );
    }

    public componentWillUnmount(): void {
        store.repositorySites.discardMutableCopy(FormUpdateSite.name, this.props.id);
    }

    @computed private get site(): Site | undefined {
        return store.repositorySites.mutableCopyById(FormUpdateSite.name, this.props.id);
    }

    @computed private get siteUpdate(): SiteUpdate {
        return pick(["label"], this.site);
    }

    @action.bound
    private async updateSite(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        evt.preventDefault();
        const site = await store.repositorySites.update(this.props.id, this.siteUpdate);
        this.props.onUpdate(site);
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formUpdateSite.submit.text")}
                validation={store.repositorySites.validation.update}
                validationId={this.validationId}
            />
        );
        return (
            <form onSubmit={this.updateSite}>
                <Stack tokens={{ padding: `0px ${sizes.formPaddingHorizontal}` }}>
                    <TextField
                        label={store.i18n.t("formUpdateSite.label.label")}
                        disabled={!this.site}
                        {...doubleBindString(this.site!, "label", () => this.triggerValidation())}
                        required
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositorySites.validation.update.getFieldValidationState(
                                this.validationId,
                                "label",
                            ),
                        )}
                    />
                </Stack>
                {this.props.asDialogContent ? (
                    <ElofleetDialogFooter>
                        <DefaultButton
                            text={store.i18n.t("formUpdateSite.cancel.text")}
                            onClick={this.props.onDialogCancel}
                        />
                        {primaryButton}
                    </ElofleetDialogFooter>
                ) : (
                    <Stack horizontal horizontalAlign="end">
                        {primaryButton}
                    </Stack>
                )}
            </form>
        );
    }
}
